import { format, isValid, parse, type Locale } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { dateMatchModifiers, type Matcher } from 'react-day-picker';

import { cn } from '@shared/utils/utils';
import { Calendar } from '@shared/components/ui/shadcn/calendar';
import { Button } from '../button/Button';
import { Input } from '../input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { MonthPicker } from './MonthPicker';

interface DatePickerProps {
  date?: Date;
  editable?: boolean;
  calendarLabel?: string;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Días no seleccionables (react-day-picker Matcher). Quedan visualmente deshabilitados. */
  disabledDates?: Matcher | Matcher[];
  /** Mes inicial del calendario al abrir. */
  defaultMonth?: Date;
  className?: string;
  'data-testid'?: string;
  id?: string;
  error?: boolean;
  mode?: 'single' | 'month';
  formatDisplay?: string;
  /** Locale usada tanto por el valor visible como por el calendario. */
  locale?: Locale;
}

function DatePicker({
  date,
  editable = false,
  calendarLabel,
  onDateChange,
  placeholder = 'Seleccionar fecha',
  disabled = false,
  disabledDates,
  defaultMonth,
  className,
  'data-testid': testId,
  id,
  error = false,
  mode = 'single',
  formatDisplay,
  locale = es,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string | null>(null);
  const inputFormat = formatDisplay || 'dd/MM/yyyy';
  const draftDate = draft ? parse(draft, inputFormat, new Date()) : undefined;
  const invalidDraft =
    draft !== null &&
    draft !== '' &&
    (!draftDate ||
      !isValid(draftDate) ||
      format(draftDate, inputFormat) !== draft ||
      (disabledDates && dateMatchModifiers(draftDate, disabledDates)));

  if (mode === 'month') {
    return (
      <MonthPicker
        date={date}
        onDateChange={onDateChange}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        data-testid={testId}
        id={id}
        error={error}
      />
    );
  }

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDraft(null);
    onDateChange?.(selectedDate);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn(editable && 'relative', editable && className)}>
        {editable && (
          <Input
            id={id}
            data-testid={testId}
            value={draft ?? (date ? format(date, inputFormat) : '')}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={error || invalidDraft || undefined}
            className={cn('pr-10', (error || invalidDraft) && 'border-destructive')}
            onChange={(event) => {
              const next = event.target.value;
              setDraft(next);
              const parsed = parse(next, inputFormat, new Date());
              onDateChange?.(
                isValid(parsed) &&
                  format(parsed, inputFormat) === next &&
                  (!disabledDates || !dateMatchModifiers(parsed, disabledDates))
                  ? parsed
                  : undefined,
              );
            }}
          />
        )}
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-start text-left font-normal h-10 px-3 py-2 bg-card-bg border-card-border',
              !date && 'text-muted-foreground',
              error && 'border-destructive focus-visible:ring-destructive',
              editable ? 'absolute right-0 top-0 h-full w-10 justify-center border-0 bg-transparent p-0' : className,
            )}
            disabled={disabled}
            data-testid={editable ? undefined : testId}
            id={editable ? undefined : id}
            aria-label={editable ? calendarLabel || placeholder : undefined}
          >
            <CalendarIcon className={cn('h-4 w-4', !editable && 'mr-2')} />
            {!editable && (date ? format(date, formatDisplay || 'PPP', { locale }) : <span>{placeholder}</span>)}
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          captionLayout="label"
          disabled={disabledDates}
          defaultMonth={defaultMonth ?? date}
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
}
DatePicker.displayName = 'DatePicker';

export { DatePicker, type DatePickerProps };
